/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { CacheType, IAction, IActionContext, IAny, IGeneric } from '@msdyn365-commerce/core';
import { AttributeValue, CategoryPathLookup } from '@msdyn365-commerce/retail-proxy';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ActiveCartProductsInput, getActiveCartProductsAction } from '@msdyn365-commerce-modules/retail-actions';
import { hasArray } from '../utils/helper';

/**
 * GetCartProductsAttributes Input Action
 */
export class GetCartProductsAttributesInput implements Msdyn365.IActionInput {
    public channelId: number;
    public catalogId: number;

    constructor(channelId: number, catalogId?: number) {
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key

    public getCacheKey = () => `AttributesForSelectedVariant`;
    public getCacheObjectType = () => 'AttributesForSelectedVariant';
    public dataCacheType = (): CacheType => 'request';
}
export interface IProductWithAttribute {
    productId: number;
    attributeValue: AttributeValue[];
    Name: string | undefined;
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetCartProductsAttributesData {
    text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createAttributesForSelectedVariantInput = (
    inputData: Msdyn365.ICreateActionContext<IGeneric<IAny>>
): GetCartProductsAttributesInput => {
    return new GetCartProductsAttributesInput(+inputData.requestContext.apiSettings.channelId);
};

export async function getAttributesForCartLineItems(
    input: GetCartProductsAttributesInput,
    ctx: IActionContext
): Promise<IProductWithAttribute[]> {
    const productAttributes: IProductWithAttribute[] = [];

    const products = (await getActiveCartProductsAction(new ActiveCartProductsInput(), ctx)).filter(Boolean);
    const catPaths: CategoryPathLookup[] = [];
    const idArray: any[] = [];
    products.map(items => idArray.push(items.RecordId));
    if (!hasArray(idArray)) {
        return [];
    }
    for (let productsIter = 0; productsIter < idArray.length; productsIter++) {
        catPaths.push({ ProductId: idArray[productsIter] });
    }
    for (let i = 0; i < products?.length; i++) {
        const productId = products[i].RecordId;
        const Name = products[i].Name;
        try {
            const productSpecificationData: AttributeValue[] = await getAttributeValuesAsync(
                { callerContext: ctx, queryResultSettings: {} },
                productId,
                ctx.requestContext.apiSettings.channelId,
                ctx.requestContext.apiSettings.catalogId
            );
            const attributeValue = productSpecificationData.filter(Boolean);
            productAttributes.push({
                productId,
                attributeValue,
                Name
            });
        } catch (error) {
            throw new Error(`Unable to get data:  ${error}`);
        }
    }

    return productAttributes;
}

export default Msdyn365.createObservableDataAction({
    // TODO: Give your data action a unique id
    id: '@msdyn365-commerce-modules/retail-actions/get-product-attributes-cartlines',
    action: <IAction<IProductWithAttribute[]>>getAttributesForCartLineItems,
    input: createAttributesForSelectedVariantInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
